import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { composerDisclaimer, homeQuickActions, homeScreenCopy } from '../mock'
import { useConversations } from '@/features/conversations/ConversationsProvider'
import { ActiveScopeBadge, ScopeSelector } from '@/features/scope'
import {
  ReferenceCountBadge,
  ReferencePicker,
  useReferenceSelection,
} from '@/features/references'
import { AttachFileTrigger } from '@/features/ingestion'

const TEXTAREA_MAX_PX = 180

/**
 * Prompt-first home state. Owns its own input that calls
 * ``ConversationsProvider.sendMessage`` — that helper will create a new
 * conversation if none is active and post the first message. No fake
 * assistant content is ever rendered here.
 */
export function HomePromptScreen() {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const sendBtnRef = useRef<HTMLButtonElement>(null)
  const scopeBtnRef = useRef<HTMLButtonElement>(null)
  const refsBtnRef = useRef<HTMLButtonElement>(null)
  const [value, setValue] = useState('')
  const [scopeOpen, setScopeOpen] = useState(false)
  // References can be picked BEFORE the first message, exactly like the Scope
  // selector. Selection itself lives in ReferenceSelectionProvider (above the
  // shell), so it survives the Home → Composer transition and is read by
  // ConversationsProvider.sendMessage at send time. Only the popover's
  // open-state is local here, mirroring `scopeOpen`.
  const [refsOpen, setRefsOpen] = useState(false)
  const { sendMessage, sending, sendError } = useConversations()
  const { selectedIds: selectedReferenceIds } = useReferenceSelection()

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_PX)}px`
  }, [value])

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  const inFlightRef = useRef(false)
  const submit = useCallback(async () => {
    if (inFlightRef.current) return
    const text = value.trim()
    if (!text || sending) return
    inFlightRef.current = true
    setValue('')
    sendBtnRef.current?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(.92)' }, { transform: 'scale(1)' }],
      { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' },
    )
    try {
      await sendMessage(text)
    } finally {
      inFlightRef.current = false
    }
  }, [value, sending, sendMessage])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        void submit()
      }
    },
    [submit],
  )

  const handleChipClick = useCallback((prompt: string) => {
    setValue(prompt)
    requestAnimationFrame(() => textareaRef.current?.focus())
  }, [])

  const hasText = value.trim().length > 0

  return (
    <div className="relative grid h-full min-h-0 w-full flex-1 place-items-center overflow-y-auto px-6 py-12">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[560px] w-[880px] max-w-full -translate-x-1/2 -translate-y-1/2 animate-home-glow"
        style={{
          background:
            'radial-gradient(closest-side, rgba(124,92,255,0.22), rgba(124,92,255,0.06) 55%, transparent 75%)',
          filter: 'blur(48px)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[62%] -z-0 h-[280px] w-[680px] max-w-full -translate-x-1/2 -translate-y-1/2"
        style={{
          background:
            'radial-gradient(closest-side, rgba(91,141,239,0.12), transparent 70%)',
          filter: 'blur(64px)',
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-[820px] flex-col items-center text-center animate-home-rise">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-text-mute backdrop-blur-md">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-violet"
            style={{ boxShadow: '0 0 8px var(--violet-glow)' }}
            aria-hidden="true"
          />
          {homeScreenCopy.eyebrow}
        </div>

        <h1
          className="m-0 px-2 text-balance text-[clamp(30px,4.6vw,48px)] font-medium leading-[1.08] tracking-[-0.02em] text-text"
          style={{ textWrap: 'balance' }}
        >
          {homeScreenCopy.titleLead}{' '}
          <span className="font-serif italic font-normal text-violet">
            {homeScreenCopy.titleAccent}
          </span>
          <span className="text-text">{homeScreenCopy.titleTail}</span>
        </h1>

        <p className="mx-auto mt-4 max-w-xl text-[14px] leading-relaxed text-text-soft">
          {homeScreenCopy.subtitle}
        </p>

        <div className="relative mt-10 w-full">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -inset-4 rounded-[32px] opacity-80"
            style={{
              background:
                'radial-gradient(60% 80% at 50% 100%, rgba(124,92,255,0.35), transparent 70%)',
              filter: 'blur(28px)',
            }}
          />

          <div
            className="relative rounded-3xl border border-white/[0.09] bg-white/[0.055] backdrop-blur-2xl transition-[border-color,box-shadow] duration-200 ease-smooth focus-within:border-violet/50"
            style={{
              boxShadow:
                '0 38px 100px -32px rgba(124,92,255,0.55), 0 14px 38px -22px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.07)',
              WebkitBackdropFilter: 'blur(24px) saturate(140%)',
              backdropFilter: 'blur(24px) saturate(140%)',
            }}
          >
            <div className="flex items-end gap-3 px-4 pt-3.5 pb-3">
              <AttachFileTrigger
                title="Anexar arquivo ao chat"
                ariaLabel="Anexar arquivo"
                className="mb-1 grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-text-soft transition-[background-color,color,border-color] duration-150 ease-smooth hover:border-white/20 hover:bg-white/[0.08] hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
                disabled={sending}
              >
                <Icon name="plus" size={16} />
              </AttachFileTrigger>

              <textarea
                ref={textareaRef}
                data-testid="composer-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={homeScreenCopy.placeholder}
                aria-label="Mensagem"
                rows={1}
                disabled={sending}
                className="flex-1 resize-none bg-transparent py-2 text-left text-[15px] leading-[1.55] text-text placeholder:text-text-dim"
                style={{ height: 40, maxHeight: TEXTAREA_MAX_PX, outline: 'none' }}
              />

              <button
                ref={sendBtnRef}
                type="button"
                data-testid="composer-send"
                disabled={!hasText || sending}
                onClick={() => void submit()}
                aria-label="Enviar mensagem"
                title="Enviar (Ctrl+Enter)"
                className="mb-1 grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl text-white transition-[transform,box-shadow,opacity] duration-150 ease-smooth disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:-translate-y-0.5 enabled:hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(180deg, var(--violet) 0%, var(--violet-deep) 100%)',
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.2), 0 8px 22px -6px var(--violet-glow)',
                }}
              >
                <Icon name="arrow-up" size={16} />
              </button>
            </div>

            <div
              className="flex flex-wrap items-center gap-1 px-3 pb-2.5 pt-1"
              style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
            >
              <AttachFileTrigger
                title="Anexar arquivo ao chat"
                ariaLabel="Anexar arquivo"
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] text-text-soft transition-colors duration-150 hover:bg-white/[0.05] hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
                disabled={sending}
              >
                <Icon name="paperclip" size={13} /> Anexar
              </AttachFileTrigger>
              <HomeToolButton icon="mic" label="Ditar" title="Ditar (em breve)" />
              <button
                ref={scopeBtnRef}
                type="button"
                data-testid="scope-trigger"
                title="Definir escopo do RAG"
                aria-haspopup="dialog"
                aria-expanded={scopeOpen}
                onClick={() => setScopeOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] text-text-soft transition-colors duration-150 hover:bg-white/[0.05] hover:text-text"
              >
                <Icon name="grid" size={13} />
                Escopo
              </button>
              <button
                ref={refsBtnRef}
                type="button"
                data-testid="references-trigger"
                title="Selecionar referências para esta consulta"
                aria-haspopup="dialog"
                aria-expanded={refsOpen}
                onClick={() => setRefsOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] text-text-soft transition-colors duration-150 hover:bg-white/[0.05] hover:text-text"
              >
                <Icon name="book" size={13} />
                Referências
                {selectedReferenceIds.length > 0 && (
                  <span
                    className="ml-0.5 rounded-md border border-violet/40 bg-violet/[0.18] px-1.5 py-0.5 font-mono text-[10px] text-violet"
                    aria-label={`${selectedReferenceIds.length} referências selecionadas`}
                  >
                    {selectedReferenceIds.length}
                  </span>
                )}
              </button>
              <ActiveScopeBadge onOpen={() => setScopeOpen(true)} />
              <ReferenceCountBadge onOpen={() => setRefsOpen(true)} />
              <div className="ml-auto font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-mute">
                {sending ? 'Enviando…' : 'Ctrl+Enter para enviar'}
              </div>
            </div>

            <ScopeSelector
              open={scopeOpen}
              onClose={() => setScopeOpen(false)}
              anchorRef={scopeBtnRef}
            />
            <ReferencePicker
              open={refsOpen}
              onClose={() => setRefsOpen(false)}
              anchorRef={refsBtnRef}
            />
          </div>

          {sendError && (
            <p className="mt-3 text-center text-[12px] text-red-200" role="alert">
              {sendError}
            </p>
          )}

          <p className="mt-4 text-center text-[11px] tracking-[0.01em] text-text-dim">
            {composerDisclaimer}
          </p>
        </div>

        <div className="mt-10 flex w-full flex-col items-center gap-3">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-text-mute">
            Comece com
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {homeQuickActions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={() => handleChipClick(action.prompt)}
                title={action.prompt}
                className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-4 py-2 text-[12.5px] font-medium text-text-soft backdrop-blur-md transition-[background-color,border-color,color,transform] duration-150 ease-smooth hover:-translate-y-0.5 hover:border-violet/40 hover:bg-white/[0.07] hover:text-text"
              >
                <Icon
                  name={action.icon}
                  size={13}
                  className="text-text-mute transition-colors duration-150 group-hover:text-violet"
                />
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface HomeToolButtonProps {
  icon: IconName
  label: string
  title: string
}

function HomeToolButton({ icon, label, title }: HomeToolButtonProps) {
  return (
    <button
      type="button"
      title={title}
      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] text-text-soft transition-colors duration-150 hover:bg-white/[0.05] hover:text-text"
    >
      <Icon name={icon} size={13} />
      {label}
    </button>
  )
}
